import { useCallback, useEffect, useRef, useState } from 'react';

/*
 * Field-level form validation for the inventory forms (strains, plates,
 * slants, DNA, external intake).
 *
 * Every check is a pure function answering null when the value is fine, or an
 * error object when it is not: { code, ...values }. `code` is stable and names
 * a message under `validation` in the common catalogue, so validationMessage()
 * can say it in the UI language; a page that wants its own wording for one
 * field ("a strain needs a species") adds `key`, a translation key of its own.
 *
 * Blank is a separate question from range. numberInRange() and checkBand()
 * let a blank value through and required() is what rejects one -- the same
 * split the server's checks and the database's CHECK constraints draw (NULL
 * passes a CHECK). So a field that is optional but bounded uses the first, a
 * field that must be filled uses the second, and one that is both uses both.
 *
 * The page keeps the errors as { [field]: error }, in the order the fields
 * appear on screen: that order is the focus order (focusFirstInvalid).
 */

/** Every code a check here can answer. Each has a message in common.json. */
export const VALIDATION_CODES = [
  'required', 'notANumber', 'notInteger',
  'outOfRange', 'tooSmall', 'tooLarge', 'aboveMin',
  'minAboveMax', 'optBelowMin', 'optAboveMax',
];

/** True for null, undefined, NaN, and a string with nothing but spaces in it. */
export function isBlank(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === 'number') return Number.isNaN(value);
  if (typeof value === 'string') return value.trim() === '';
  return false;
}

/** A value as a number: null when blank, NaN when it is not a number. */
export function toNumber(value) {
  if (isBlank(value)) return null;
  if (typeof value === 'number') return value;
  const text = String(value).trim();
  // Number('') is 0 and Number('0x1A') is 26; neither is what a person typed.
  if (!/^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i.test(text)) return NaN;
  return Number(text);
}

const withKey = (error, key) => (key ? { ...error, key } : error);

/** null when the value is filled in; otherwise { code: 'required' }. */
export function required(value, key) {
  return isBlank(value) ? withKey({ code: 'required' }, key) : null;
}

/**
 * A number within bounds. Blank passes (use required() for that).
 *
 *   min, max      inclusive bounds; either may be left out
 *   minExclusive  min itself is not allowed (a count of days must be above 0)
 *   integer       a whole number only
 */
export function numberInRange(value, { min, max, minExclusive = false, integer = false } = {}, key) {
  const n = toNumber(value);
  if (n === null) return null;
  if (!Number.isFinite(n)) return withKey({ code: 'notANumber' }, key);
  if (integer && !Number.isInteger(n)) return withKey({ code: 'notInteger' }, key);
  const hasMin = min !== undefined && min !== null;
  const hasMax = max !== undefined && max !== null;
  const belowMin = hasMin && (minExclusive ? n <= min : n < min);
  const aboveMax = hasMax && n > max;
  if (belowMin) {
    if (minExclusive) return withKey({ code: 'aboveMin', min }, key);
    return withKey(hasMax ? { code: 'outOfRange', min, max } : { code: 'tooSmall', min }, key);
  }
  if (aboveMax) {
    return withKey(hasMin && !minExclusive ? { code: 'outOfRange', min, max } : { code: 'tooLarge', max }, key);
  }
  return null;
}

/**
 * The order inside a band: min <= max and, when there is an optimum,
 * min <= opt <= max. `fields` names the three keys of `values` ({ min, opt,
 * max }; opt may be left out). Only ends that are numbers are compared --
 * a blank or non-numeric end is another check's business.
 *
 * Returns { [field]: error }: the minimum carries 'minAboveMax', the optimum
 * carries 'optBelowMin' / 'optAboveMax'. Mirrors routes/strains.py's
 * _requirement_problem and migration 0001's CHECK constraints.
 */
export function checkBand(values, { min, opt, max }) {
  const errors = {};
  const lo = toNumber(values[min]);
  const hi = toNumber(values[max]);
  const mid = opt ? toNumber(values[opt]) : null;
  const ok = (n) => n !== null && Number.isFinite(n);
  if (ok(lo) && ok(hi) && lo > hi) errors[min] = { code: 'minAboveMax' };
  if (opt && ok(mid)) {
    if (ok(lo) && mid < lo) errors[opt] = { code: 'optBelowMin' };
    else if (ok(hi) && mid > hi) errors[opt] = { code: 'optAboveMax' };
  }
  return errors;
}

/**
 * The errors that are not null, keyed by field, in the order given. The
 * first error a field gets is the one kept, so list the more basic check
 * (required, a number at all) before the finer one (in range, in its band).
 */
export function collectErrors(pairs) {
  const out = {};
  for (const [field, error] of pairs) {
    if (error && !(field in out)) out[field] = error;
  }
  return out;
}

export const hasErrors = (errors) => !!errors && Object.keys(errors).length > 0;

/** The errors without `field`, or without every field `match` accepts. */
export function withoutErrors(errors, match) {
  if (!hasErrors(errors)) return errors || {};
  const drop = typeof match === 'function' ? match : (f) => f === match;
  const out = {};
  for (const [field, error] of Object.entries(errors)) if (!drop(field)) out[field] = error;
  return Object.keys(out).length === Object.keys(errors).length ? errors : out;
}

/** An error in the UI language: the page's own key if it gave one, else the shared message. */
export function validationMessage(t, error) {
  if (!error) return '';
  const { code, key, ...values } = error;
  return t(key || `common:validation.${code}`, values);
}

/**
 * A server refusal in the UI language. A refusal whose `code` the page knows
 * gets that message (with the response body as its interpolation values);
 * anything else gets the page's fallback. The server's own `error` sentence is
 * never shown: it is English, and written for whoever reads the logs.
 */
export function serverErrorMessage(t, err, codeKeys = {}, fallbackKey = 'common:saveFailed') {
  const data = err?.response?.data || {};
  const key = data.code && Object.prototype.hasOwnProperty.call(codeKeys, data.code)
    ? codeKeys[data.code] : null;
  return key ? t(key, data) : t(fallbackKey);
}

const FOCUSABLE = 'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The element to focus for a field: the element itself when it can take focus, else the first control inside it. */
export function focusTarget(el) {
  if (!el) return null;
  if (typeof el.matches === 'function' && el.matches(FOCUSABLE)) return el;
  return typeof el.querySelector === 'function' ? el.querySelector(FOCUSABLE) : null;
}

/**
 * A form's field errors, and the focus that follows them.
 *
 *   errors        { [field]: error } -- render each beside its field
 *   show(errors)  replace them; focus moves to the first once they are on
 *                 screen, so the field it lands on is already described by
 *                 its message when a screen reader announces it
 *   clear(match)  drop one field's error (a name, or a test on names) --
 *                 call it when the person edits that field
 *   reset()       drop them all
 *   containerRef  put on the form (or panel) to search for data-field
 *                 markers; left unset, the whole document is searched
 */
export function useFieldErrors() {
  const [errors, setErrors] = useState({});
  const containerRef = useRef(null);
  const focusPending = useRef(false);
  useEffect(() => {
    if (!focusPending.current) return;
    focusPending.current = false;
    focusFirstInvalid(containerRef.current || document, errors);
  }, [errors]);
  const show = useCallback((next) => {
    focusPending.current = hasErrors(next);
    setErrors(next || {});
  }, []);
  const clear = useCallback((match) => setErrors((current) => withoutErrors(current, match)), []);
  const reset = useCallback(() => setErrors((current) => (hasErrors(current) ? {} : current)), []);
  return { errors, show, clear, reset, containerRef };
}

/**
 * Move focus to the first field in `errors` -- the field marked
 * data-field="<name>" inside `container` (a form, a panel, or document).
 * Answers the field it focused, or null.
 */
export function focusFirstInvalid(container, errors) {
  if (!container || !hasErrors(errors)) return null;
  for (const field of Object.keys(errors)) {
    const marked = container.querySelector(`[data-field="${String(field).replace(/["\\]/g, '\\$&')}"]`);
    const target = focusTarget(marked);
    if (target) {
      target.focus();
      if (typeof target.scrollIntoView === 'function') target.scrollIntoView({ block: 'center' });
      return field;
    }
  }
  return null;
}
